/**
 * Consumer (individual) Pro billing snapshot, as returned on `consumerBilling`
 * from `GET /accounts/user`.
 */
export interface ConsumerBilling {
  planTier: 'PRO' | 'FREE';
  proBillingInterval: 'month' | 'year' | null;
  /** Billing rail that owns the active Pro: 'stripe' (web), 'google' / 'apple' (native store), or
   * 'revenuecat' (native, store unknown); null if not Pro. */
  proProvider?: 'stripe' | 'google' | 'apple' | 'revenuecat' | null;
  stripeCurrentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionStatus: string | null;
  /** True when the user has a Stripe customer (so the portal can be opened). */
  stripePortalAvailable?: boolean;
}

/**
 * Stripe subscription statuses that keep paid access — mirrors the server's
 * STRIPE_SUBSCRIPTION_STATUS_ACTIVE_SET so the client and DB agree on "active".
 */
const ACTIVE_STRIPE_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing', 'past_due']);

/**
 * Whether the user has a real, manageable Stripe subscription — the gate for showing
 * "Manage subscription" (which opens the Stripe Customer Portal). Requires both a Stripe
 * customer (so the portal can open) and a paid/active subscription status. A bare
 * `planTier === 'PRO'` is intentionally NOT enough: PRO granted without an active Stripe
 * sub (e.g. native/manual, or a canceled-then-stale flag) must not show the portal CTA.
 */
export function isManageableConsumerSub(billing: ConsumerBilling | null | undefined): boolean {
  if (!billing) return false;
  if (billing.stripePortalAvailable !== true) return false;
  const status = billing.stripeSubscriptionStatus;
  return status != null && ACTIVE_STRIPE_STATUSES.has(status);
}
